import { useCallback, useEffect, useState } from 'react';

import { ApiError, apiRequest } from '../api/client.js';
import type { BotCommand, BotSettings, BotVariable } from '../api/types.js';
import { Alert } from '../components/Alert.js';
import { EmptyState } from '../components/EmptyState.js';
import { StatusPill } from '../components/StatusPill.js';
import { useSession } from '../auth/session-context.js';
import { BotCommands } from './bot/BotCommands.js';
import { BotLogs } from './bot/BotLogs.js';
import { BotOverview } from './bot/BotOverview.js';
import { BotResponses } from './bot/BotResponses.js';
import { BotTestConsole } from './bot/BotTestConsole.js';
import { BotTimers } from './bot/BotTimers.js';
import './bot.css';

/**
 * Connaro Bot, in the Console (ARCHITECTURE.md §102).
 *
 * ==================== WHAT THIS PAGE WILL NOT DO ====================
 * Decide anything. Which platforms exist, which of them the bot may speak on,
 * which variables a response may contain and whether the workspace's plan
 * includes the module are all answered by the server and rendered as given.
 *
 * There is no list of variables typed into this file and no per-platform
 * capability table — `GET /bot/variables` supplies the first and the Test
 * Console's response supplies the second, for the same reason the chat
 * composer holds no per-platform limits: a claim compiled into a bundle is one
 * nothing keeps current.
 * ====================================================================
 *
 * ==================== TWO SWITCHES, NEVER ONE ====================
 * "Bot enabled for Twitch" and "Twitch will accept a send" are different facts
 * owned by different parties, and §102 forbids collapsing them. The Overview
 * renders both, and the word "Active" appears only where both are true.
 * =================================================================
 */

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'commands', label: 'Commands' },
  { id: 'timers', label: 'Timers' },
  { id: 'responses', label: 'Responses' },
  { id: 'test', label: 'Test Console' },
  { id: 'logs', label: 'Logs' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function BotPage(): React.JSX.Element {
  const { activeWorkspace, can } = useSession();
  const workspaceId = activeWorkspace?.id ?? null;

  const [tab, setTab] = useState<TabId>('overview');
  const [settings, setSettings] = useState<BotSettings | null>(null);
  const [commands, setCommands] = useState<BotCommand[] | null>(null);
  const [variables, setVariables] = useState<BotVariable[]>([]);
  const [error, setError] = useState<string | null>(null);
  /*
   * The plan gate, kept apart from every other failure.
   *
   * A 402 is not an error the operator did anything to cause, and rendering it
   * as one ("Something went wrong") would send them to support for a billing
   * question. The API answers 402 precisely so this can be told apart from a
   * 403 and from a genuine fault.
   */
  const [notEntitled, setNotEntitled] = useState(false);

  const mayManage = can('bot.manage');
  const mayViewLogs = can('bot.logs.view');
  const mayTest = can('bot.test');

  const load = useCallback(async (): Promise<void> => {
    setError(null);

    try {
      const [loadedSettings, loadedCommands, loadedVariables] = await Promise.all([
        apiRequest<BotSettings>('/bot/settings'),
        apiRequest<{ commands: BotCommand[] }>('/bot/commands'),
        apiRequest<{ variables: BotVariable[] }>('/bot/variables'),
      ]);

      setSettings(loadedSettings);
      setCommands(loadedCommands.commands);
      setVariables(loadedVariables.variables);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'ENTITLEMENT_REQUIRED') {
        setNotEntitled(true);
        return;
      }
      setError(
        caught instanceof ApiError ? caught.message : 'Connaro could not read the bot settings.',
      );
    }
  }, []);

  useEffect(() => {
    // Nothing from the previous workspace survives the switch (§13).
    setSettings(null);
    setCommands(null);
    setNotEntitled(false);
    setError(null);
    void load();
  }, [load, workspaceId]);

  if (notEntitled) {
    return (
      <section className="panel" aria-labelledby="bot-heading">
        <div className="panel__header">
          <h1 className="panel__title" id="bot-heading">
            Bot
          </h1>
        </div>
        <div className="panel__body">
          <EmptyState title="Not included in this plan">
            <p>
              Connaro Bot answers commands, posts timers and replies automatically in your chat. It
              is included from the Pro plan upwards. Nothing is lost by upgrading later —
              configuration you add is kept.
            </p>
          </EmptyState>
        </div>
      </section>
    );
  }

  return (
    <section className="panel bot" aria-labelledby="bot-heading">
      <div className="panel__header bot__header">
        <h1 className="panel__title" id="bot-heading">
          Bot
        </h1>
        {settings !== null && (
          <span className="bot__status">
            {/*
             * The word AND the state, never colour alone (§65). "Off" is a
             * complete sentence to somebody who cannot see the pill.
             */}
            <StatusPill
              tone={settings.enabled ? 'ok' : 'idle'}
              label={settings.enabled ? 'On' : 'Off'}
            />
          </span>
        )}
      </div>

      <div className="panel__body bot__body">
        {error !== null && <Alert tone="error">{error}</Alert>}

        <nav className="bot__tabs" aria-label="Bot sections">
          <ul className="bot__tablist">
            {TABS.map((entry) => {
              /*
               * A tab the actor cannot use is not rendered. §4.2 makes the
               * backend authoritative, and a Logs tab that 403s is an
               * invitation to fail rather than a control.
               */
              if (entry.id === 'logs' && !mayViewLogs) return null;
              if (entry.id === 'test' && !mayTest) return null;

              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    className={`bot__tab${tab === entry.id ? ' bot__tab--active' : ''}`}
                    aria-current={tab === entry.id ? 'page' : undefined}
                    onClick={() => {
                      setTab(entry.id);
                    }}
                  >
                    {entry.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {settings === null ? (
          <p className="field__hint">Loading…</p>
        ) : (
          <>
            {tab === 'overview' && (
              <BotOverview
                settings={settings}
                commands={commands ?? []}
                mayManage={mayManage}
                onChanged={load}
              />
            )}
            {tab === 'commands' && (
              <BotCommands
                commands={commands ?? []}
                variables={variables}
                settings={settings}
                onChanged={load}
              />
            )}
            {tab === 'timers' && <BotTimers settings={settings} variables={variables} />}
            {tab === 'responses' && <BotResponses settings={settings} variables={variables} />}
            {tab === 'test' && mayTest && <BotTestConsole settings={settings} />}
            {tab === 'logs' && mayViewLogs && <BotLogs />}
          </>
        )}
      </div>
    </section>
  );
}
